/**
 * Migração de catálogos locais (localStorage) para a nuvem (API própria).
 *
 * Espelha as assinaturas do antigo módulo Supabase (referência histórica).
 */
import { isCloudConfigured } from './client';
import { importCatalogToRemote } from './catalog';
import type { CatalogFile } from '../../types/catalog';

export interface MigrationResult {
  migrated: number;
  skipped: number;
  errors: Array<{ catalogName: string; error: string }>;
}

const STORAGE_KEY = 'prevdrive-catalogs';
const MIGRATED_FLAG = 'cloud-migrated';

export async function migrateLocalCatalogsToSupabase(): Promise<MigrationResult> {
  if (!isCloudConfigured()) {
    return { migrated: 0, skipped: 0, errors: [{ catalogName: '', error: 'API não configurada' }] };
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return { migrated: 0, skipped: 0, errors: [] };

  let entries: Array<[string, CatalogFile]>;
  try {
    entries = JSON.parse(stored);
  } catch {
    return { migrated: 0, skipped: 0, errors: [{ catalogName: '', error: 'Erro ao ler localStorage' }] };
  }

  const result: MigrationResult = { migrated: 0, skipped: 0, errors: [] };

  for (const [, catalog] of entries) {
    try {
      const { skipped } = await importCatalogToRemote(catalog);
      if (skipped) result.skipped++;
      else result.migrated++;
    } catch (err) {
      result.errors.push({ catalogName: catalog.catalogName, error: String(err) });
    }
  }

  if (result.migrated > 0 || result.skipped > 0) {
    localStorage.setItem(MIGRATED_FLAG, 'true');
  }

  return result;
}

export function hasMigrationRun(): boolean {
  return localStorage.getItem(MIGRATED_FLAG) === 'true';
}

export function clearMigrationFlag(): void {
  localStorage.removeItem(MIGRATED_FLAG);
}

export async function forceMigrate(): Promise<MigrationResult> {
  clearMigrationFlag();
  return migrateLocalCatalogsToSupabase();
}
